// Canonical shapes for the cross-device video state sync feature.
// This module has no runtime deps so it can be imported safely from server,
// web client, and iPhone native runtime without dragging in Prisma, React,
// or browser-only globals.

export interface ProgressRow {
  videoId: string;
  watchedSeconds: number;
  clientUpdatedAt: string;
  // ISO timestamp of the first time this user watched the video through to
  // `mobileWatchedThreshold`, or null if they never have.
  //
  // Read this — never `watchedSeconds / durationSeconds` — to answer "have they
  // finished it?". `watchedSeconds` is a live playhead that last-write-wins, so
  // it drops back when the viewer scrubs backwards through a video they already
  // completed. Server-owned and sticky; clients only read it.
  completedAt: string | null;
  /**
   * Client timestamp of the last MANUAL completion verdict, or null if the
   * funder has never set one by hand.
   *
   * It exists so a merge can order completion INDEPENDENTLY of the playhead.
   * `completedAt` alone can't: "no completion" reads identically whether the
   * video was never finished or the funder just cleared it, so a position
   * response carrying an older value looked like new information in one case
   * and stale information in the other. Every guess about which — take it,
   * keep the cache, advance only — was wrong in some direction. With a clock
   * there is nothing to guess: newer verdict wins.
   */
  completionUpdatedAt: string | null;
}

export interface BookmarkRow {
  videoId: string;
  bookmarked: boolean;
  clientUpdatedAt: string;
}

export interface PlaylistRow {
  videoId: string;
  position: number;
  clientUpdatedAt: string;
}

export interface GetUserVideoStateResponse {
  progress: ProgressRow[];
  bookmarks: BookmarkRow[];
  // Retired feature, load-bearing field. Saved clips are gone, but TestFlight
  // bundles installed before their removal still run
  // `for (const row of server.clipBookmarks)` inside applyServerHydration —
  // which would throw on `undefined`, and that loop sits BEFORE the single
  // commit that applies progress, bookmarks and the playlist. Hydration's
  // caller only warns on a throw, so those clients would silently stop
  // syncing altogether until they updated. Always empty (typed so it can't be
  // repopulated); delete once no pre-retirement install is in the field.
  clipBookmarks: never[];
  playlist: PlaylistRow[];
  // Per-user sentinel: the clientUpdatedAt of the last playlist mutation.
  // Needed to LWW the playlist even when it's empty (a cleared playlist has
  // no entry rows to carry a timestamp). Null when the user has never
  // mutated their playlist.
  playlistClientUpdatedAt: string | null;
}

// Cadence for client → server watch-progress writes. Used by both the
// iPhone WatchProgressThrottle and the web VideoStatesProvider's lodash
// throttle so behaviour is consistent across platforms.
export const watchProgressThrottleMs = 30_000;
